/**
 * Plain words for a failed save on the Settings page (SETTINGS-ERR-1, BL-f4c4eb60).
 *
 * Worded by HTTP status, never the server's own text (same pattern as approvalAnswerError in
 * services/approvalService.ts); the caller writes the technical detail to the console.
 * Kept free of the API client so the server tests can load it (server/test/push-settings.test.ts).
 */

export type SettingsSaveTarget = 'channels' | 'preferences';

const WHAT_WAS_NOT_SAVED: Record<SettingsSaveTarget, string> = {
  channels: 'The notification channel settings were not saved',
  preferences: 'Your default notification preferences were not saved',
};

const REASON_BY_STATUS: Record<number, string> = {
  401: 'because your sign-in has expired. Sign out, sign in again, then save again.',
  403: 'because your account cannot change these settings. Ask an Admin.',
  429: 'because too many requests were sent at once. Wait a minute, then save again.',
};

const SERVER_FAULT_REASON = 'because of a problem on the server. Your changes are still on this page; try saving again in a minute.';
const REFUSED_REASON = 'because the server did not accept them. Check the values on this page, then save again.';
const NO_ANSWER_REASON = 'because the server could not be reached. Check your connection, then save again.';
const FIRST_SERVER_FAULT_STATUS = 500;

function statusOf(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

/** The sentence to show when saving `target` failed with `error`. */
export function settingsSaveError(target: SettingsSaveTarget, error: unknown): string {
  const status = statusOf(error);
  let reason: string;
  if (status === undefined) reason = NO_ANSWER_REASON;
  else reason = REASON_BY_STATUS[status] ?? (status >= FIRST_SERVER_FAULT_STATUS ? SERVER_FAULT_REASON : REFUSED_REASON);
  return `${WHAT_WAS_NOT_SAVED[target]} ${reason}`;
}
